import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNodes } from "@/api/queries/nodes";
import { useSetAssignment } from "@/api/mutations/agents";
import type { Agent } from "@/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent: Agent;
}

const UNASSIGN = "__unassign__";

export function SetAssignmentDialog({ open, onOpenChange, agent }: Props) {
  const nodesQuery = useNodes();
  const mutation = useSetAssignment();
  const [selected, setSelected] = useState<string>(agent.assignment?.nodeId ?? UNASSIGN);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelected(agent.assignment?.nodeId ?? UNASSIGN);
      setServerError(null);
    }
  }, [open, agent.assignment?.nodeId]);

  function handleSubmit() {
    const nodeId = selected === UNASSIGN ? null : selected;
    mutation.mutate(
      { id: agent.id, nodeId },
      {
        onSuccess: () => {
          toast.success(nodeId ? `Assignment → ${nodeId}` : "Assignment entfernt");
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = (err as Error).message ?? "Unbekannter Fehler";
          setServerError(msg);
          toast.error(`Assignment fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="assignment-dialog">
        <DialogHeader>
          <DialogTitle>Assignment ändern</DialogTitle>
          <DialogDescription>Node auf dem der Agent laufen soll.</DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <Label>Node</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="assignment-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNASSIGN}>— unassigned</SelectItem>
              {(nodesQuery.data ?? []).map((n) => (
                <SelectItem key={n.id} value={n.id}>
                  {n.hostname} ({n.id})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {serverError && (
          <div className="text-sm text-red" data-testid="assignment-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="assignment-cancel">
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending} data-testid="assignment-submit">
            {mutation.isPending ? "..." : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
